import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message } from "antd";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import { competitors, partners } from "../../config";

// Import componenti e funzioni ausiliarie
import { orgCoordsQuery } from "./queries";
import { calculateDistance, filterCoordinates, calculateStats } from "./utils";
import SidePanel from "./SidePanel";
import MapContent from "./MapContent";
import LoadingSpinner from "./LoadingSpinner";

export default function MappaClienti() {
  const [orgCoords, setOrgCoords] = useState([]);
  const [filteredOrgCoords, setFilteredOrgCoords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("attiva");
  const [tipoIstanzaFilter, setTipoIstanzaFilter] = useState("production");
  const [useStatusFilter, setUseStatusFilter] = useState(true);
  const [useTipoIstanzaFilter, setUseTipoIstanzaFilter] = useState(true);
  const [useProximityFilter, setUseProximityFilter] = useState(false);
  const [proximityRange, setProximityRange] = useState(30);
  const [debouncedProximityRange, setDebouncedProximityRange] = useState(30);
  const [userLocation, setUserLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState(null);
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  const [showCompetitors, setShowCompetitors] = useState(true);
  const [showPartners, setShowPartners] = useState(true);
  const [totals, setTotals] = useState({
    all: 0,
    attiva: 0,
    disattiva: 0,
    production: 0,
    demo: 0,
    free: 0
  });
  
  const mapRef = useRef(null);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  
  // Debounce function for proximity range changes
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedProximityRange(proximityRange);
    }, 500); // 500ms debounce delay
    
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [proximityRange]);

  // Fetch organization coordinates
  useEffect(() => {
    const fetchOrgCoords = async () => {
      try {
        setLoading(true);
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: orgCoordsQuery,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.org_coords?.edges) {
          const orgsWithCoords = response.data.org_coords.edges
            .map(edge => edge.node)
            .filter(coord => 
              // Filter out invalid coordinates
              coord.latitude && 
              coord.longitude && 
              coord.org_pointer
            );
          
          // Calculate totals
          const newTotals = calculateStats(orgsWithCoords);
          
          setTotals(newTotals);
          setOrgCoords(orgsWithCoords);
          
          // Apply initial filters
          const filtered = filterCoordinates(
            orgsWithCoords, 
            statusFilter, 
            tipoIstanzaFilter, 
            useStatusFilter, 
            useTipoIstanzaFilter, 
            useProximityFilter,
            debouncedProximityRange,
            userLocation,
            useCustomLocation,
            customLocation,
            calculateDistance
          );
          
          setFilteredOrgCoords(filtered);
        } else {
          setError("Nessuna coordinata trovata");
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching org coordinates:", error);
        setError("Errore nel caricamento dei dati");
        setLoading(false);
      }
    };

    fetchOrgCoords();
  }, [
    getTokenSilently, 
    env,
    statusFilter,
    tipoIstanzaFilter,
    useStatusFilter,
    useTipoIstanzaFilter,
    useProximityFilter,
    debouncedProximityRange,
    userLocation,
    useCustomLocation,
    customLocation
  ]);

  // Apply filters when they change
  useEffect(() => {
    // Imposta il flag di caricamento per filtraggio
    setFiltering(true);

    // Utilizziamo requestAnimationFrame per migliorare le prestazioni
    // e simulare un caricamento più fluido
    requestAnimationFrame(() => {
      const filtered = filterCoordinates(
        orgCoords, 
        statusFilter, 
        tipoIstanzaFilter, 
        useStatusFilter, 
        useTipoIstanzaFilter, 
        useProximityFilter,
        debouncedProximityRange,
        userLocation,
        useCustomLocation,
        customLocation,
        calculateDistance
      );
      
      setFilteredOrgCoords(filtered);
      
      // Timeout breve per dare feedback visivo anche se il filtraggio è veloce
      setTimeout(() => {
        setFiltering(false);
      }, 300);
    });
  }, [
    orgCoords, 
    statusFilter, 
    tipoIstanzaFilter, 
    useStatusFilter, 
    useTipoIstanzaFilter, 
    useProximityFilter,
    debouncedProximityRange,
    userLocation,
    useCustomLocation,
    customLocation
  ]);

  // Handle filter changes
  const handleStatusChange = (value) => {
    if (value === "tutti") {
      setUseStatusFilter(false);
    } else {
      setStatusFilter(value);
      setUseStatusFilter(true);
    }
  };

  const handleTipoIstanzaChange = (value) => {
    if (value === "tutti") {
      setUseTipoIstanzaFilter(false);
    } else {
      setTipoIstanzaFilter(value);
      setUseTipoIstanzaFilter(true);
    }
  };

  // Handle proximity range change
  const handleProximityRangeChange = (value) => {
    if (value !== null) {
      setProximityRange(value);
    }
  };

  // Get user's geolocation
  const getUserLocation = () => {
    setLoadingLocation(true);
    
    if (!navigator.geolocation) {
      message.error("Geolocalizzazione non supportata dal tuo browser");
      setLoadingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setUserLocation(userCoords);
        setUseProximityFilter(true);
        message.success("Posizione rilevata correttamente");
        setLoadingLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        message.error("Impossibile ottenere la tua posizione. Verifica le autorizzazioni.");
        setLoadingLocation(false);
      },
      { maximumAge: 60000, timeout: 5000, enableHighAccuracy: true }
    );
  };

  // Toggle use of user's location
  const useMyLocation = () => {
    if (!userLocation) {
      getUserLocation();
    }
    setUseCustomLocation(false);
    setIsSelectingLocation(false);
    setUseProximityFilter(true);
  };
  
  // Start custom location selection mode
  const startCustomLocationSelection = () => {
    message.info('Clicca un punto sulla mappa per impostare il centro del raggio');
    setIsSelectingLocation(true);
    setUseCustomLocation(true);
    setUseProximityFilter(true);
  };
  
  // Handle map click to set custom location
  const handleMapClick = (latlng) => {
    setCustomLocation({
      lat: latlng.lat,
      lng: latlng.lng
    });
    setIsSelectingLocation(false);
    message.success('Posizione personalizzata impostata correttamente');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Card
      title="Mappa Clienti"
      extra={error && <span style={{ color: "red" }}>{error}</span>}
      bodyStyle={{ padding: '12px' }}
    >
      <Row gutter={[16, 16]}>
        {/* Layout a due colonne responsive */}
        <Col xs={24} md={8} lg={6}>
          <SidePanel 
            statusFilter={statusFilter}
            tipoIstanzaFilter={tipoIstanzaFilter}
            useStatusFilter={useStatusFilter}
            useTipoIstanzaFilter={useTipoIstanzaFilter}
            useProximityFilter={useProximityFilter}
            proximityRange={proximityRange}
            userLocation={userLocation}
            customLocation={customLocation}
            useCustomLocation={useCustomLocation}
            isSelectingLocation={isSelectingLocation}
            loadingLocation={loadingLocation}
            filteredOrgCoords={filteredOrgCoords}
            totals={totals}
            orgCoords={orgCoords}
            handleStatusChange={handleStatusChange}
            handleTipoIstanzaChange={handleTipoIstanzaChange}
            handleProximityRangeChange={handleProximityRangeChange}
            setUseProximityFilter={setUseProximityFilter}
            useMyLocation={useMyLocation}
            startCustomLocationSelection={startCustomLocationSelection}
            filtering={filtering}
            showCompetitors={showCompetitors}
            setShowCompetitors={setShowCompetitors}
            showPartners={showPartners}
            setShowPartners={setShowPartners}
          />
        </Col>
        
        {/* Mappa sulla destra */}
        <Col xs={24} md={16} lg={18}>
          <MapContent 
            filteredOrgCoords={filteredOrgCoords}
            customLocation={customLocation}
            useCustomLocation={useCustomLocation}
            isSelectingLocation={isSelectingLocation}
            proximityRange={proximityRange}
            handleMapClick={handleMapClick}
            mapRef={mapRef}
            filtering={filtering}
            showCompetitors={showCompetitors}
            competitors={competitors}
            showPartners={showPartners}
            partners={partners}
          />
        </Col>
      </Row>
    </Card>
  );
}